// Clasificación A/A+ de señales en el FRONTEND (WP A/A+ fase 2, 2026-09-29)
// -- espejo EXACTO de backend/app/core/signal_class.py (fase 1, merge
// 7081598). El docstring de ese módulo lo exige: misma fórmula y mismas
// constantes en los dos sitios; si algún día cambia f o m, cambian AMBOS en
// el mismo WP (el test signalClass.test.js lo fuerza con los casos del
// backend y con un fixture generado por la función Python real).
//
// Fórmula: una señal de entrada (long/short) es A+ ⟺ d_tp >= m × f, donde
//   d_tp = |TP1 − entrada| / entrada × 100   (TP1 = el TP más cercano)
//   f    = coste round-trip en % (comisión de IDA + VUELTA del exchange)
//   m    = multiplicador exigido (margen sobre el coste)
//
// Mismo orden de operaciones que Python (abs(tp1 - entry) / entry * 100.0):
// ambos lenguajes usan doubles IEEE-754, así que el mismo orden da el mismo
// bit -- el límite exacto (d_tp == m × f → A+) cae igual en los dos lados.
//
// La clase SOLO ETIQUETA (decisión de Marc, addendum 2026-09-29): en chart
// y Telegram las señales A y A+ se muestran siempre; ninguna se oculta.

// Coste por LADO en %, por exchange y modo (backend EXCHANGE_FEE_PROFILE).
export const EXCHANGE_FEE_PROFILE = Object.freeze({
  bitunix: Object.freeze({ taker_side_pct: 0.06, maker_side_pct: 0.02 }),
})
export const FEE_MODE = 'taker'
export const DEFAULT_M = 2.0

export function roundTripFeePct(exchange = 'bitunix', mode = FEE_MODE) {
  const side = EXCHANGE_FEE_PROFILE[exchange]?.[`${mode}_side_pct`]
  if (typeof side !== 'number') throw new Error(`fee profile ${exchange}/${mode} no dado de alta`)
  return 2.0 * side
}

// f por defecto del chart = el que usan tarjeta y bots (taker Bitunix, 0.12).
export const DEFAULT_F = roundTripFeePct()

// float() de Python sobre lo que llega del motor: null/undefined no son 0
// (Number(null) === 0 en JS) -- se tratan como ausentes, igual que el
// `if x is not None` del backend.
function toFloat(v) {
  if (v === null || v === undefined) return null
  if (typeof v === 'number') return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isNaN(n)) throw new Error('not a number')
    return n
  }
  throw new Error('unsupported type')
}

/**
 * Clasifica una señal de entrada como "A" o "A+" (función pura). Mismo
 * contrato que classify_signal: devuelve SIEMPRE
 * { class, d_tp, f, m, reason } y NUNCA lanza.
 *  - reason null en clasificación normal; "invalid_entry" | "no_tp" |
 *    "classification_error" cuando la clase es A por datos o por fallo.
 */
export function classifySignal(entry, tp1, { f = null, m = null } = {}) {
  let fEff = f ?? null
  const mEff = m ?? DEFAULT_M
  try {
    if (fEff === null) fEff = roundTripFeePct()
    const entryF = toFloat(entry)
    if (entryF === null || !Number.isFinite(entryF) || entryF <= 0) {
      return { class: 'A', d_tp: null, f: fEff, m: mEff, reason: 'invalid_entry' }
    }
    const tp1F = toFloat(tp1)
    if (tp1F === null || !Number.isFinite(tp1F)) {
      return { class: 'A', d_tp: null, f: fEff, m: mEff, reason: 'no_tp' }
    }
    const dTp = Math.abs(tp1F - entryF) / entryF * 100.0
    const cls = dTp >= mEff * fEff ? 'A+' : 'A'
    return { class: cls, d_tp: dTp, f: fEff, m: mEff, reason: null }
  } catch {
    return { class: 'A', d_tp: null, f: fEff ?? 0.0, m: mEff, reason: 'classification_error' }
  }
}

/** classify_from_levels: TP1 = take_profits[0].price del dict de niveles. */
export function classifyFromLevels(levels, entry) {
  let tp1 = null
  try {
    const tps = levels?.take_profits ?? []
    if (tps.length) tp1 = tps[0]?.price ?? null
  } catch {
    tp1 = null
  }
  return classifySignal(entry, tp1)
}

// ── Presentación (chart) ────────────────────────────────────────────────
// Textos espejo del caption de la tarjeta de Telegram
// (signal_card_tasks._fire_signal): " · A+ · expectancy positiva" /
// " · A · seguimiento manual recomendado".
export const CLASS_TOOLTIP = Object.freeze({
  'A+': 'A+ · expectancy positiva',
  A: 'A · seguimiento manual recomendado',
})

// Config del chart (m y f en %): valores del usuario saneados. Un valor
// no numérico, negativo o no finito vuelve al default -- nunca NaN en la
// comparación (NaN >= x es false → todo saldría A en silencio). m = 0 o
// f = 0 son válidos (umbral 0: toda señal con TP es A+).
export function resolveClassParams(cfg) {
  const num = (v, d) => {
    const n = typeof v === 'string' ? Number(v.replace(',', '.')) : v
    return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : d
  }
  return { m: num(cfg?.signalClassM, DEFAULT_M), f: num(cfg?.signalClassFeePct, DEFAULT_F) }
}

// true si el chart clasifica con otros m/f que tarjeta y bots -- el panel
// lo avisa: la etiqueta del chart dejaría de coincidir con la de Telegram.
export function classParamsDiffer(params) {
  return params.m !== DEFAULT_M || params.f !== DEFAULT_F
}

/**
 * Etiqueta A/A+ para un marcador de señal del chart. Devuelve null si no
 * hay nada que etiquetar (sin entrada válida) -- nunca oculta la señal,
 * solo decide si el marcador lleva badge.
 */
export function signalClassBadge(entry, tp1, params) {
  const r = classifySignal(entry, tp1, params ?? {})
  if (r.reason === 'invalid_entry') return null
  return { cls: r.class, tooltip: CLASS_TOOLTIP[r.class], dTp: r.d_tp }
}
